/**
 * Supprime les fuites de raisonnement `<think>`/`<thinking>` dans le canal
 * TEXTE de l'agent (C2, harness non muet — post-mortem Dana 2026-08).
 *
 * WHY : le canal de raisonnement (`reasoning-delta` côté transport,
 * `streamThinking` côté add-in) est déjà séparé et NE PASSE PAS par cette
 * passe. Un modèle qui laisse fuiter du texte entre `<think>` et `</think>`
 * dans le canal TEXTE écrit du raisonnement anglais brut que l'avocat
 * francophone ne doit jamais lire — symétrique de `toolCallLeak.ts` (appels
 * d'outils écrits en prose) pour la même classe de dérive : le modèle mélange
 * deux canaux qui doivent rester disjoints.
 *
 * Trois formes, toutes traitées par le marqueur STRUCTUREL (la balise
 * elle-même), jamais par une détection de langue — un `<think>`/`</think>`
 * n'a AUCUNE légitimité dans une prose juridique française, la présence de la
 * balise suffit à qualifier tout ce qu'elle encadre comme fuite :
 *  1. Bloc complet `<think>…</think>` (ou `<thinking>…</thinking>`) : retiré
 *     en bloc, y compris son contenu.
 *  2. Opener NON refermé en fin de buffer (flux coupé en pleine génération) :
 *     tout ce qui suit l'opener EST du raisonnement, tronqué jusqu'à la fin —
 *     même logique que `toolCallLeak` pour un `<invoke>` non refermé.
 *  3. `</think>` ORPHELIN (opener jamais émis dans CE segment — il vivait dans
 *     le vrai canal reasoning, seule la fin du raisonnement a fuité dans le
 *     texte) : tout ce qui PRÉCÈDE la balise dans le segment en est le
 *     reliquat, visiblement du raisonnement (c'est la balise qui le prouve,
 *     pas une analyse de langue) — retiré jusqu'à et y compris la balise.
 */

export interface ThinkTagLeak {
  /** Longueur du bloc retiré — jamais son contenu (raisonnement, non journalisable). */
  length: number;
}

export interface ThinkTagLeakResult {
  /** Texte débarrassé des blocs de raisonnement fuités. */
  text: string;
  /** Un élément par bloc retiré. Vide = pas de fuite. */
  leaks: ThinkTagLeak[];
}

/** Fin de nom de balise : espace, `/`, `>` ou fin de chaîne (flux coupé). */
const TAG_NAME_END = '(?=[\\s/>]|$)';

const THINK_BLOCK_RE = new RegExp(`<(thinking|think)${TAG_NAME_END}[^>]*>[\\s\\S]*?</\\1\\s*>`, 'gi');
const THINK_OPENER_RE = new RegExp(`<(?:thinking|think)${TAG_NAME_END}[^>]*>?`, 'i');
const THINK_ORPHAN_CLOSER_RE = /<\/(?:thinking|think)\s*>/i;

/**
 * Marqueur posé à la place d'un bloc retiré, zone privée Unicode — jamais NUL
 * (Postgres refuse 0x00, ce texte finit en base via `MessageRepository.saveMessage`).
 */
const CUT = '\uE000';

/**
 * Retire les fuites de raisonnement `<think>`/`<thinking>` d'un segment de
 * PROSE. Ne pas appeler sur du code : passer par `normalizeAssistantText`, qui
 * isole les fences au préalable (fence-safe par construction du pipeline).
 */
export function thinkTagLeak(text: string): ThinkTagLeakResult {
  if (!text || text.indexOf('<') === -1) return { text, leaks: [] };

  const leaks: ThinkTagLeak[] = [];
  let out = text;

  // 1. Blocs <think>…</think> / <thinking>…</thinking> complets.
  out = out.replace(THINK_BLOCK_RE, (block) => {
    leaks.push({ length: block.length });
    return CUT;
  });

  // 2. Opener non refermé : la génération a été coupée en plein raisonnement.
  const opener = THINK_OPENER_RE.exec(out);
  if (opener) {
    leaks.push({ length: out.length - opener.index });
    out = out.slice(0, opener.index);
  }

  // 3. </think> orphelin : le reliquat de raisonnement qui le précède part avec lui.
  const orphanCloser = THINK_ORPHAN_CLOSER_RE.exec(out);
  if (orphanCloser) {
    const cutEnd = orphanCloser.index + orphanCloser[0].length;
    leaks.push({ length: cutEnd });
    out = out.slice(cutEnd);
  }

  if (leaks.length === 0) return { text, leaks };

  // Le retrait laisse des trous : une ligne qui ne portait QUE des blocs
  // retirés disparaît entièrement, un marqueur au milieu d'une phrase s'efface
  // sur place (même traitement que toolCallLeak.ts). `.trim()` (pas seulement
  // `trimEnd()`) : contrairement à un appel d'outil, un </think> orphelin peut
  // laisser TOUT le reliquat au DÉBUT du segment (cf. cas 3 ci-dessus), ce qui
  // laisserait des lignes vides en tête sans le trim symétrique.
  out = out
    .split('\n')
    .filter((line) => !(line.includes(CUT) && line.split(CUT).join('').trim() === ''))
    .join('\n')
    .split(CUT)
    .join('')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return { text: out, leaks };
}
